import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Reveal from "@/components/Reveal";

const SKYLINE_IMG =
  "https://images.unsplash.com/photo-1661527188297-35e762c4519b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxwcmVtaXVtJTIwaW5kaWElMjBtb2Rlcm4lMjBza3lsaW5lJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc5MTAzNTA0OHww&ixlib=rb-4.1.0&q=85";

const STATS = [
  { k: "1,600+", v: "GCCs already operate from India" },
  { k: "40–60%", v: "Structural cost advantage vs. onshore" },
  { k: "GMT+5:30", v: "Follow-the-sun coverage, built in" },
];

export default function WhyOffshore() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="why-offshore" ref={ref} className="border-b border-line bg-ink text-paper">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-24 md:grid-cols-12 md:py-32">
        <div className="md:col-span-6">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-paper/50">
              03 — Why Offshore
            </p>
            <h2
              className="mt-6 font-serif text-4xl leading-tight tracking-tight md:text-5xl"
              data-testid="why-offshore-headline"
            >
              India is not a cost decision. It's a{" "}
              <em className="text-goldlight">capability</em> decision.
            </h2>
            <p className="mt-8 max-w-lg text-base leading-relaxed text-paper/70">
              The question is no longer whether to build in India — it is who
              runs the build. A GCC gives you the talent density of India with
              the control of your own balance sheet, culture and roadmap.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-px border border-paper/20 bg-paper/20 sm:grid-cols-3">
            {STATS.map((s, i) => (
              <Reveal key={s.k} delay={i * 0.1} className="bg-ink">
                <div className="p-6" data-testid={`stat-${i + 1}`}>
                  <p className="font-serif text-2xl md:text-3xl">{s.k}</p>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/50">
                    {s.v}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="md:col-span-6">
          <Reveal delay={0.2}>
            <div className="relative h-[380px] overflow-hidden border border-paper/20 md:h-[520px]">
              <motion.img
                style={{ y }}
                src={SKYLINE_IMG}
                alt="Modern India skyline"
                className="h-full w-full scale-125 object-cover contrast-105 saturate-110"
                data-testid="why-offshore-image"
              />
              <div className="absolute inset-0 bg-gold/10 mix-blend-multiply" />
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/40">
              Bengaluru — 12.9716° N, 77.5946° E
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
